import { ChangeEvent, FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { RequiredMark } from '../../components/ui/RequiredMark';
import { SupportAiSuggestButton } from '../../components/customerSuccess/SupportAiSuggestButton';
import { TicketHistoryPanel } from '../../components/customerSuccess/TicketHistoryPanel';
import { VirtualScrollColumn } from '../../components/customerSuccess/VirtualScrollColumn';
import { formCardClass } from '../../utils/formCardClass';
import { useMessageBox } from '../../contexts/MessageBoxContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { Company, listCompanies } from '../../services/coreCompaniesApi';
import { Contact, listContacts } from '../../services/coreContactsApi';
import {
  CatalogFormField,
  ServiceCatalogItem,
  listActiveServiceCatalogItems,
  parseCatalogFormSchema,
} from '../../services/serviceCatalogApi';
import {
  ServiceTicket,
  ServiceTicketPipeline,
  ServiceTicketSummary,
  ServiceTicketSlaOption,
  ServiceTicketTeam,
  ServiceTicketTeamMember,
  ServiceTicketUpsertPayload,
  addServiceTicketComment,
  createServiceTicket,
  deleteServiceTicketAttachment,
  getServiceTicket,
  getServiceTicketAttachmentDownloadUrl,
  getServiceTicketPipeline,
  listServiceTickets,
  listTicketInboundMessages,
  listTicketSlaOptions,
  listTicketTeamMembers,
  listTicketTeams,
  suggestServiceTicketMessage,
  updateServiceTicket,
  updateServiceTicketStatus,
  uploadServiceTicketAttachment,
} from '../../services/customerSuccessTicketsApi';
import { listSalesProducts, SalesProductSummary } from '../../services/salesProductsApi';
import { listActiveContractsByCompany, Contract } from '../../services/commercialContractsApi';
import { listOpenProblemsByCompany, ServiceProblem } from '../../services/serviceProblemsApi';
import { SupportInboundMessage } from '../../services/supportInboundApi';
import { OPERATIONAL_TICKET_STAGES, TICKET_STAGES, TicketStageId } from '../../types/TicketStage';

type TicketsViewMode = 'board' | 'history';

const SOURCE_CHANNEL_LABELS: Record<string, string> = {
  Internal: 'Interno',
  Email: 'E-mail',
  Chat: 'Chat',
  Api: 'API',
  Portal: 'Portal',
  Widget: 'Widget',
};

function formatSourceChannel(sourceChannel: string): string {
  return SOURCE_CHANNEL_LABELS[sourceChannel] ?? sourceChannel;
}

const INBOUND_STATUS_LABELS: Record<string, string> = {
  Pending: 'Pendente',
  Processing: 'Processando',
  Processed: 'Processado',
  Ignored: 'Ignorado',
  Failed: 'Falhou',
};

const LEVEL_OPTIONS = [
  { value: '', label: 'Não informado' },
  { value: 'Low', label: 'Baixa' },
  { value: 'Medium', label: 'Média' },
  { value: 'High', label: 'Alta' },
  { value: 'Critical', label: 'Crítica' },
] as const;

const BOARD_PAGE_SIZE = 30;

type TicketColumnState = {
  items: ServiceTicketSummary[];
  page: number;
  totalCount: number;
  loadingMore: boolean;
};

function emptyColumnState(): TicketColumnState {
  return { items: [], page: 0, totalCount: 0, loadingMore: false };
}

const PRIORITY_LABELS: Record<string, string> = {
  Low: 'Baixa',
  Medium: 'Média',
  High: 'Alta',
  Critical: 'Crítica',
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const emptyPayload: ServiceTicketUpsertPayload = {
  title: '',
  description: '',
  ticketType: 'Incident',
  companyId: '',
  status: 'New',
  priority: 'Medium',
  sourceChannel: 'Internal',
};

function parseFormData(json?: string | null): Record<string, string> {
  if (!json?.trim()) return {};
  try {
    const parsed = JSON.parse(json) as Record<string, unknown>;
    return Object.fromEntries(
      Object.entries(parsed).map(([key, value]) => [key, value == null ? '' : String(value)]),
    );
  } catch {
    return {};
  }
}

export function TicketsPage() {
  const { confirmDelete } = useMessageBox();
  const { canWriteModule } = usePermissions();
  const canWrite = canWriteModule('customer-success.tickets');
  const [searchParams, setSearchParams] = useSearchParams();

  const viewMode: TicketsViewMode = searchParams.get('view') === 'history' ? 'history' : 'board';

  const [pipeline, setPipeline] = useState<ServiceTicketPipeline | null>(null);
  const [columns, setColumns] = useState<Record<TicketStageId, TicketColumnState>>(() =>
    Object.fromEntries(OPERATIONAL_TICKET_STAGES.map((stage) => [stage.id, emptyColumnState()])) as Record<
      TicketStageId,
      TicketColumnState
    >,
  );
  const [linkableTickets, setLinkableTickets] = useState<ServiceTicketSummary[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [teamMembers, setTeamMembers] = useState<ServiceTicketTeamMember[]>([]);
  const [teams, setTeams] = useState<ServiceTicketTeam[]>([]);
  const [slaOptions, setSlaOptions] = useState<ServiceTicketSlaOption[]>([]);
  const [catalogItems, setCatalogItems] = useState<ServiceCatalogItem[]>([]);
  const [salesProducts, setSalesProducts] = useState<SalesProductSummary[]>([]);
  const [companyContracts, setCompanyContracts] = useState<Contract[]>([]);
  const [companyProblems, setCompanyProblems] = useState<ServiceProblem[]>([]);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ServiceTicket | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceTicket | null>(null);
  const [form, setForm] = useState<ServiceTicketUpsertPayload>(emptyPayload);
  const [commentBody, setCommentBody] = useState('');
  const [commentSending, setCommentSending] = useState(false);
  const commentSendingRef = useRef(false);
  const [commentInternal, setCommentInternal] = useState(false);
  const [suggestingComment, setSuggestingComment] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [inboundMessages, setInboundMessages] = useState<SupportInboundMessage[]>([]);
  const [inboundTotalCount, setInboundTotalCount] = useState(0);
  const [boardSearchDraft, setBoardSearchDraft] = useState('');
  const [boardSearch, setBoardSearch] = useState('');

  const loadInboundForTicket = useCallback(async (ticketId: string): Promise<void> => {
    try {
      const result = await listTicketInboundMessages(ticketId);
      setInboundMessages(result.items);
      setInboundTotalCount(result.totalCount);
    } catch {
      setInboundMessages([]);
      setInboundTotalCount(0);
    }
  }, []);

  const loadColumn = useCallback(
    async (
      stageId: TicketStageId,
      page: number,
      append: boolean,
      search: string,
    ): Promise<void> => {
      if (page > 1) {
        setColumns((current) => ({
          ...current,
          [stageId]: { ...current[stageId], loadingMore: true },
        }));
      }

      try {
        const result = await listServiceTickets({
          status: stageId,
          search: search || undefined,
          page,
          pageSize: BOARD_PAGE_SIZE,
        });

        setColumns((current) => ({
          ...current,
          [stageId]: {
            items: append ? [...current[stageId].items, ...result.items] : result.items,
            page: result.page,
            totalCount: result.totalCount,
            loadingMore: false,
          },
        }));
      } catch {
        setColumns((current) => ({
          ...current,
          [stageId]: { ...current[stageId], loadingMore: false },
        }));
      }
    },
    [],
  );

  const loadPipeline = useCallback(async (search: string): Promise<void> => {
    try {
      setPipeline(await getServiceTicketPipeline(search || undefined));
    } catch {
      setPipeline(null);
    }
  }, []);

  const loadBoard = useCallback(
    async (silent = false): Promise<void> => {
      if (!silent) setLoading(true);
      try {
        const [linkableResult, companyResult, members, teamList, slas, catalog, products] =
          await Promise.all([
            listServiceTickets({ page: 1, pageSize: 200 }),
            listCompanies({ activeOnly: true, page: 1, pageSize: 200 }),
            listTicketTeamMembers(),
            listTicketTeams(),
            listTicketSlaOptions(),
            listActiveServiceCatalogItems(),
            listSalesProducts({ activeOnly: true, page: 1, pageSize: 200 }),
          ]);

        setLinkableTickets(linkableResult.items);
        setCompanies(companyResult.items);
        setTeamMembers(members);
        setTeams(teamList);
        setSlaOptions(slas);
        setCatalogItems(catalog);
        setSalesProducts(products.items);

        await loadPipeline(boardSearch);
        await Promise.all(
          OPERATIONAL_TICKET_STAGES.map((stage) => loadColumn(stage.id, 1, false, boardSearch)),
        );
      } catch {
        toast.error('Falha ao carregar chamados.');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [boardSearch, loadColumn, loadPipeline],
  );

  const loadMoreColumn = useCallback(
    (stageId: TicketStageId): void => {
      const column = columns[stageId];
      if (column.loadingMore || column.items.length >= column.totalCount) return;
      void loadColumn(stageId, column.page + 1, true, boardSearch);
    },
    [boardSearch, columns, loadColumn],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => setBoardSearch(boardSearchDraft.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [boardSearchDraft]);

  useEffect(() => {
    if (viewMode !== 'board') return;
    void loadBoard();
  }, [loadBoard, viewMode]);

  useEffect(() => {
    if (viewMode !== 'history' || companies.length > 0) return;
    void listCompanies({ activeOnly: true, page: 1, pageSize: 200 })
      .then((result) => setCompanies(result.items))
      .catch(() => {});
  }, [companies.length, viewMode]);

  useEffect(() => {
    const selectedId = searchParams.get('selected');
    if (!selectedId || selected?.id === selectedId) return;
    void (async () => {
      try {
        const ticket = await getServiceTicket(selectedId);
        setSelected(ticket);
        await loadInboundForTicket(selectedId);
      } catch {
        toast.error('Falha ao carregar ticket.');
      }
    })();
  }, [loadInboundForTicket, searchParams, selected?.id]);

  useEffect(() => {
    if (!form.companyId) {
      setContacts([]);
      setCompanyContracts([]);
      setCompanyProblems([]);
      return;
    }
    void (async () => {
      try {
        const [contactResult, contracts, problems] = await Promise.all([
          listContacts({ companyId: form.companyId, activeOnly: true, pageSize: 100 }),
          listActiveContractsByCompany(form.companyId),
          listOpenProblemsByCompany(form.companyId),
        ]);
        setContacts(contactResult.items);
        setCompanyContracts(contracts);
        setCompanyProblems(problems);
      } catch {
        setContacts([]);
        setCompanyContracts([]);
        setCompanyProblems([]);
      }
    })();
  }, [form.companyId]);

  async function refreshSelected(ticketId: string): Promise<void> {
    const ticket = await getServiceTicket(ticketId);
    setSelected(ticket);
    setSearchParams({ selected: ticketId });
    await loadInboundForTicket(ticketId);
  }

  function handleSelect(ticket: ServiceTicketSummary): void {
    void refreshSelected(ticket.id);
  }

  function openCreate(): void {
    setEditing(null);
    setForm(emptyPayload);
    setFormData({});
    setFormOpen(true);
  }

  function openEdit(ticket: ServiceTicket): void {
    setEditing(ticket);
    setForm({
      title: ticket.title,
      description: ticket.description ?? '',
      ticketType: ticket.ticketType,
      companyId: ticket.companyId,
      contactId: ticket.contactId ?? undefined,
      assignedTeamId: ticket.assignedTeamId ?? undefined,
      assignedToUserId: ticket.assignedToUserId ?? undefined,
      slaId: ticket.slaId ?? undefined,
      serviceCatalogItemId: ticket.serviceCatalogItemId ?? undefined,
      status: ticket.status,
      priority: ticket.priority,
      impact: ticket.impact ?? undefined,
      urgency: ticket.urgency ?? undefined,
      parentTicketId: ticket.parentTicketId ?? undefined,
      contractId: ticket.contractId ?? undefined,
      problemId: ticket.problemId ?? undefined,
      salesProductId: ticket.salesProductId ?? undefined,
      sourceChannel: ticket.sourceChannel,
    });
    setFormData(parseFormData(ticket.formDataJson));
    setFormOpen(true);
  }

  const selectedCatalogItem = catalogItems.find((item) => item.id === form.serviceCatalogItemId);
  const catalogFormSchema = parseCatalogFormSchema(selectedCatalogItem?.formSchemaJson);

  function handleCatalogChange(catalogItemId: string): void {
    const item = catalogItems.find((catalogItem) => catalogItem.id === catalogItemId);
    setForm({
      ...form,
      ticketType: 'Request',
      serviceCatalogItemId: catalogItemId || undefined,
      slaId: item?.defaultSlaId ?? form.slaId,
      assignedTeamId: item?.defaultAssignedTeamId ?? form.assignedTeamId,
      priority: item?.defaultPriority ?? form.priority,
    });
    setFormData({});
  }

  function updateFormField(field: CatalogFormField, value: string): void {
    setFormData((current) => ({ ...current, [field.name]: value }));
  }

  function buildSubmitPayload(): ServiceTicketUpsertPayload {
    const hasFormData = Object.keys(formData).length > 0;
    return {
      ...form,
      formDataJson: hasFormData ? JSON.stringify(formData) : undefined,
    };
  }

  async function handleSave(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = buildSubmitPayload();
      if (editing) {
        const updated = await updateServiceTicket(editing.id, payload);
        toast.success('Chamado atualizado.');
        if (selected?.id === editing.id) setSelected(updated);
      } else {
        const created = await createServiceTicket(payload);
        toast.success('Chamado criado.');
        setSelected(created);
        setSearchParams({ selected: created.id });
      }
      setFormOpen(false);
      await loadBoard(true);
    } catch {
      toast.error('Falha ao salvar chamado.');
    } finally {
      setSaving(false);
    }
  }

  async function handleMove(ticketId: string, status: TicketStageId, sortOrder: number): Promise<void> {
    try {
      const updated = await updateServiceTicketStatus(ticketId, { status, sortOrder });
      if (selected?.id === ticketId) setSelected(updated);
      await loadBoard(true);
    } catch {
      toast.error('Falha ao mover chamado.');
    }
  }

  async function handleAddComment(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!selected || !commentBody.trim() || commentSendingRef.current) return;

    commentSendingRef.current = true;
    setCommentSending(true);
    try {
      await addServiceTicketComment(selected.id, { body: commentBody, isInternal: commentInternal });
      setCommentBody('');
      await refreshSelected(selected.id);
      await loadBoard(true);
      toast.success('Comentário adicionado.');
    } catch {
      toast.error('Falha ao adicionar comentário.');
    } finally {
      commentSendingRef.current = false;
      setCommentSending(false);
    }
  }

  async function handleSuggestComment(): Promise<void> {
    if (!selected || suggestingComment) return;
    setSuggestingComment(true);
    try {
      const result = await suggestServiceTicketMessage(
        selected.id,
        commentInternal ? 'InternalNote' : 'Reply',
        commentBody,
      );
      setCommentBody(result.text);
      toast.success(
        commentInternal
          ? 'Sugestão de nota interna gerada. Revise antes de registrar.'
          : 'Sugestão de comentário gerada. Revise antes de enviar.',
      );
    } catch (err) {
      const detail = (err as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      toast.error(detail ?? 'Não foi possível sugerir o texto com IA.');
    } finally {
      setSuggestingComment(false);
    }
  }

  async function handleUploadAttachment(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!selected || !file) return;

    setUploadingAttachment(true);
    try {
      await uploadServiceTicketAttachment(selected.id, file);
      await refreshSelected(selected.id);
      toast.success('Anexo enviado.');
    } catch {
      toast.error('Falha ao enviar anexo.');
    } finally {
      setUploadingAttachment(false);
    }
  }

  async function handleDownloadAttachment(attachmentId: string, fileName: string): Promise<void> {
    if (!selected) return;

    try {
      const download = await getServiceTicketAttachmentDownloadUrl(selected.id, attachmentId);
      const link = document.createElement('a');
      link.href = download.url;
      link.download = fileName;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error('Falha ao obter link de download.');
    }
  }

  async function handleDeleteAttachment(attachmentId: string): Promise<void> {
    if (!selected) return;
    if (
      !(await confirmDelete({
        title: 'Remover anexo',
        message: 'Remover este anexo do chamado?',
        confirmLabel: 'Remover',
      }))
    ) {
      return;
    }

    try {
      await deleteServiceTicketAttachment(selected.id, attachmentId);
      await refreshSelected(selected.id);
      toast.success('Anexo removido.');
    } catch {
      toast.error('Falha ao remover anexo.');
    }
  }

  function setViewMode(mode: TicketsViewMode): void {
    const next = new URLSearchParams(searchParams);
    if (mode === 'history') {
      next.set('view', 'history');
    } else {
      next.delete('view');
    }
    setSearchParams(next);
  }

  if (viewMode === 'board' && (loading || !pipeline)) {
    return <LoadingSpinner label="Carregando chamados..." />;
  }

  return (
    <PageShell>
      <PageHeader
        title="Chamados"
        description="Gestão de incidentes e solicitações com workflow, SLA e trilha de auditoria."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={viewMode === 'board' ? 'erp-btn-primary' : 'erp-btn-secondary'}
              onClick={() => setViewMode('board')}
            >
              Operacional
            </button>
            <button
              type="button"
              className={viewMode === 'history' ? 'erp-btn-primary' : 'erp-btn-secondary'}
              onClick={() => setViewMode('history')}
            >
              Histórico
            </button>
            {canWrite ? (
              <button type="button" className="erp-btn-primary" onClick={openCreate}>
                Novo chamado
              </button>
            ) : null}
          </div>
        }
      />

      {viewMode === 'board' ? (
        <>
          <div className="mb-4">
            <input
              className="erp-input max-w-xl"
              placeholder="Buscar no board (código, título, empresa, responsável...)"
              value={boardSearchDraft}
              onChange={(event) => setBoardSearchDraft(event.target.value)}
            />
          </div>

          <div className="mb-4 flex flex-wrap gap-2 text-sm text-secondary-600">
            {pipeline?.stages
              .filter((stage) => OPERATIONAL_TICKET_STAGES.some((item) => item.id === stage.status))
              .map((stage) => (
                <span key={stage.status} className="rounded-full bg-secondary-100 px-3 py-1">
                  {TICKET_STAGES.find((item) => item.id === stage.status)?.label ?? stage.status}: {stage.count}
                </span>
              ))}
          </div>

          <div className="flex max-h-[calc(100dvh-18rem)] gap-4 overflow-x-auto pb-2">
            {OPERATIONAL_TICKET_STAGES.map((stage) => {
              const column = columns[stage.id];
              const columnItems = column.items;

              return (
                <div
                  key={stage.id}
                  className={`flex min-h-[28rem] min-w-[280px] max-w-[320px] flex-1 flex-col rounded-xl border ${stage.columnClass}`}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    const ticketId = event.dataTransfer.getData('text/ticket-id');
                    if (ticketId && canWrite) void handleMove(ticketId, stage.id, columnItems.length);
                  }}
                >
                  <div className={`rounded-t-xl px-3 py-2 ${stage.headerClass}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold">{stage.label}</span>
                      <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">
                        {column.totalCount || columnItems.length}
                      </span>
                    </div>
                  </div>
                  <VirtualScrollColumn
                    items={columnItems}
                    hasMore={columnItems.length < column.totalCount}
                    loadingMore={column.loadingMore}
                    onLoadMore={() => loadMoreColumn(stage.id)}
                    getItemKey={(ticket) => ticket.id}
                    renderItem={(ticket) => (
                      <button
                        type="button"
                        draggable={canWrite}
                        onDragStart={(event) => event.dataTransfer.setData('text/ticket-id', ticket.id)}
                        onClick={() => handleSelect(ticket)}
                        className={`erp-card w-full border-l-4 p-3 text-left shadow-sm transition hover:shadow-md ${stage.cardAccentClass} ${selected?.id === ticket.id ? 'ring-2 ring-primary-400' : ''}`}
                      >
                        <p className="text-xs font-medium text-secondary-500">{ticket.ticketCode}</p>
                        <p className="font-semibold text-secondary-900 line-clamp-2">{ticket.title}</p>
                        <p className="mt-1 text-xs text-secondary-600">{ticket.companyName}</p>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-secondary-500">
                          <span>{PRIORITY_LABELS[ticket.priority] ?? ticket.priority}</span>
                          {ticket.assignedToUserName ? <span>{ticket.assignedToUserName}</span> : null}
                        </div>
                      </button>
                    )}
                  />
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <TicketHistoryPanel
          companies={companies}
          selectedId={selected?.id}
          onSelect={(ticket) => handleSelect(ticket)}
        />
      )}

      {selected ? (
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <section className="erp-card">
            <div className="erp-card-body space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <p className="text-sm text-secondary-500">{selected.ticketCode}</p>
                    <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-700">
                      {selected.sourceChannelName
                        ? `${formatSourceChannel(selected.sourceChannel)} · ${selected.sourceChannelName}`
                        : formatSourceChannel(selected.sourceChannel)}
                    </span>
                  </div>
                  <h2 className="erp-card-title">{selected.title}</h2>
                  <p className="text-sm text-secondary-600">{selected.companyName}</p>
                </div>
                {canWrite ? (
                  <button type="button" className="erp-btn-secondary" onClick={() => openEdit(selected)}>
                    Editar
                  </button>
                ) : null}
              </div>
              {selected.description ? (
                <p className="whitespace-pre-wrap text-sm text-secondary-700">{selected.description}</p>
              ) : null}
              <dl className="grid gap-2 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-secondary-500">Status</dt>
                  <dd>{TICKET_STAGES.find((s) => s.id === selected.status)?.label ?? selected.status}</dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Canal de origem</dt>
                  <dd>
                    {selected.sourceChannelName
                      ? `${formatSourceChannel(selected.sourceChannel)} · ${selected.sourceChannelName}`
                      : formatSourceChannel(selected.sourceChannel)}
                  </dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Conversa externa</dt>
                  <dd className="break-all font-mono text-xs">
                    {selected.externalConversationId ?? '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Prioridade</dt>
                  <dd>{PRIORITY_LABELS[selected.priority] ?? selected.priority}</dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Impacto</dt>
                  <dd>{selected.impact ? (PRIORITY_LABELS[selected.impact] ?? selected.impact) : '—'}</dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Urgência</dt>
                  <dd>{selected.urgency ? (PRIORITY_LABELS[selected.urgency] ?? selected.urgency) : '—'}</dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Ticket pai</dt>
                  <dd>{selected.parentTicketCode ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Produto</dt>
                  <dd>{selected.salesProductName ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Contrato</dt>
                  <dd>
                    {selected.contractCode
                      ? `${selected.contractCode} — ${selected.contractTitle ?? ''}`
                      : '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Problema</dt>
                  <dd>
                    {selected.problemCode ? (
                      <Link
                        to="/customer-success/problems"
                        className="text-primary-600 hover:underline"
                      >
                        {selected.problemCode} — {selected.problemTitle ?? ''}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Catálogo</dt>
                  <dd>{selected.serviceCatalogItemName ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Responsável</dt>
                  <dd>{selected.assignedToUserName ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-secondary-500">SLA resolução</dt>
                  <dd>
                    {selected.slaResolutionDueAtUtc
                      ? new Date(selected.slaResolutionDueAtUtc).toLocaleString('pt-BR')
                      : '—'}
                  </dd>
                </div>
              </dl>
              {inboundMessages.length > 0 || inboundTotalCount > 0 ? (
                <div className="rounded-lg border border-secondary-200 p-3 text-sm">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <p className="font-medium text-secondary-700">
                      Mensagens inbound
                      {inboundTotalCount > 0 ? ` (${inboundTotalCount})` : ''}
                    </p>
                    <Link
                      className="text-primary-600 hover:underline"
                      to={`/customer-success/inbound?ticketId=${selected.id}`}
                    >
                      Ver todas
                    </Link>
                  </div>
                  <ul className="space-y-2">
                    {inboundMessages.map((message) => (
                      <li
                        key={message.id}
                        className="rounded-md border border-secondary-100 bg-secondary-50 px-3 py-2"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-secondary-500">
                          <span>{new Date(message.receivedAtUtc).toLocaleString('pt-BR')}</span>
                          <span>
                            {message.channel} ·{' '}
                            {INBOUND_STATUS_LABELS[message.processingStatus] ?? message.processingStatus}
                          </span>
                        </div>
                        <p className="mt-1 font-medium text-secondary-800">
                          {message.fromAddress}
                        </p>
                        <p className="mt-1 line-clamp-2 text-secondary-700">
                          {message.subject ?? message.bodyText}
                        </p>
                        {message.attachmentCount > 0 ? (
                          <p className="mt-1 text-xs text-secondary-500">
                            {message.attachmentCount} anexo(s)
                          </p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : selected.sourceChannel !== 'Internal' ? (
                <p className="text-sm text-secondary-500">
                  Nenhuma mensagem inbound vinculada a este chamado.
                  {selected.externalConversationId ? (
                    <>
                      {' '}
                      Conversa:{' '}
                      <span className="font-mono text-xs">{selected.externalConversationId}</span>
                    </>
                  ) : null}
                </p>
              ) : null}
              {selected.formDataJson ? (
                <div className="rounded-lg border border-secondary-200 p-3 text-sm">
                  <p className="mb-2 font-medium text-secondary-700">Dados do formulário</p>
                  <dl className="grid gap-2 sm:grid-cols-2">
                    {Object.entries(parseFormData(selected.formDataJson)).map(([key, value]) => (
                      <div key={key}>
                        <dt className="text-secondary-500">{key}</dt>
                        <dd>{value || '—'}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ) : null}
            </div>
          </section>

          <section className="erp-card">
            <div className="erp-card-body">
              <h3 className="font-semibold text-secondary-900">Linha do tempo</h3>
              <ul className="mt-3 space-y-2 text-sm">
                {selected.events.map((event) => (
                  <li key={event.id} className="rounded-lg border border-secondary-200 px-3 py-2">
                    <p className="font-medium">{event.description}</p>
                    <p className="text-xs text-secondary-500">
                      {event.createdByUserName ?? 'Sistema'} ·{' '}
                      {new Date(event.createdAtUtc).toLocaleString('pt-BR')}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="erp-card lg:col-span-2">
            <div className="erp-card-body">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-semibold text-secondary-900">Anexos</h3>
                {canWrite ? (
                  <label className="erp-btn-secondary cursor-pointer">
                    {uploadingAttachment ? 'Enviando...' : 'Enviar arquivo'}
                    <input
                      type="file"
                      className="hidden"
                      disabled={uploadingAttachment}
                      onChange={(event) => void handleUploadAttachment(event)}
                    />
                  </label>
                ) : null}
              </div>
              <p className="mt-1 text-xs text-secondary-500">
                PDF, Office, imagens e compactados — até 50 MB. Armazenados na nuvem (MinIO / S3).
              </p>
              <ul className="mt-3 space-y-2">
                {(selected.attachments ?? []).length === 0 ? (
                  <li className="text-sm text-secondary-500">Nenhum anexo.</li>
                ) : (
                  (selected.attachments ?? []).map((attachment) => (
                    <li
                      key={attachment.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-secondary-200 px-3 py-2"
                    >
                      <div>
                        <p className="text-sm font-medium text-secondary-900">{attachment.fileName}</p>
                        <p className="text-xs text-secondary-500">
                          {formatFileSize(attachment.fileSizeBytes)} ·{' '}
                          {attachment.uploadedByUserName ?? 'Usuário'} ·{' '}
                          {new Date(attachment.createdAtUtc).toLocaleString('pt-BR')}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="erp-btn-secondary text-sm"
                          onClick={() =>
                            void handleDownloadAttachment(attachment.id, attachment.fileName)
                          }
                        >
                          Baixar
                        </button>
                        {canWrite ? (
                          <button
                            type="button"
                            className="erp-btn-danger erp-btn-sm"
                            onClick={() => void handleDeleteAttachment(attachment.id)}
                          >
                            Remover
                          </button>
                        ) : null}
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </section>

          <section className="erp-card lg:col-span-2">
            <div className="erp-card-body">
              <h3 className="font-semibold text-secondary-900">Comentários</h3>
              <div className="mt-3 space-y-3">
                {selected.comments.map((comment) => (
                  <div key={comment.id} className="rounded-lg border border-secondary-200 px-3 py-2">
                    <p className="whitespace-pre-wrap text-sm">{comment.body}</p>
                    <p className="mt-1 text-xs text-secondary-500">
                      {comment.createdByUserName ?? 'Usuário'} ·{' '}
                      {new Date(comment.createdAtUtc).toLocaleString('pt-BR')}
                      {comment.isInternal ? ' · interno' : ''}
                    </p>
                  </div>
                ))}
              </div>
              {canWrite ? (
                <form onSubmit={(event) => void handleAddComment(event)} className="mt-4 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <label className="text-xs font-medium text-secondary-700" htmlFor="ticket-comment-body">
                      {commentInternal ? 'Nota interna' : 'Comentário'}
                    </label>
                    <SupportAiSuggestButton
                      suggesting={suggestingComment}
                      onClick={() => void handleSuggestComment()}
                      title={
                        commentInternal
                          ? 'Sugerir nota interna com IA'
                          : 'Sugerir comentário com IA'
                      }
                    />
                  </div>
                  <textarea
                    id="ticket-comment-body"
                    className="erp-input min-h-[80px] w-full"
                    value={commentBody}
                    onChange={(e) => setCommentBody(e.target.value)}
                    placeholder="Adicionar comentário..."
                    disabled={suggestingComment}
                  />
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={commentInternal}
                      onChange={(e) => setCommentInternal(e.target.checked)}
                      disabled={suggestingComment}
                    />
                    Comentário interno
                  </label>
                  <button
                    type="submit"
                    className="erp-btn-primary"
                    disabled={commentSending || suggestingComment || !commentBody.trim()}
                  >
                    {commentSending ? 'Enviando...' : 'Comentar'}
                  </button>
                </form>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}

      {formOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className={formCardClass(!!editing, 'erp-card max-h-[90vh] w-full max-w-2xl overflow-y-auto')}>
            <form onSubmit={(event) => void handleSave(event)} className="erp-card-body erp-form">
              <h2 className="erp-card-title">{editing ? 'Editar chamado' : 'Novo chamado'}</h2>
              <label className="erp-label">
                Título <RequiredMark />
                <input
                  className="erp-input"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                />
              </label>
              <div className="erp-form-grid">
                <label className="erp-label">
                  Empresa <RequiredMark />
                  <select
                    className="erp-input"
                    value={form.companyId}
                    onChange={(e) => setForm({ ...form, companyId: e.target.value, contactId: undefined })}
                    required
                  >
                    <option value="">Selecione...</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="erp-label">
                  Contato
                  <select
                    className="erp-input"
                    value={form.contactId ?? ''}
                    onChange={(e) => setForm({ ...form, contactId: e.target.value || undefined })}
                  >
                    <option value="">Nenhum</option>
                    {contacts.map((contact) => (
                      <option key={contact.id} value={contact.id}>
                        {contact.fullName}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="erp-label">
                  Tipo
                  <select
                    className="erp-input"
                    value={form.ticketType}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        ticketType: e.target.value,
                        serviceCatalogItemId:
                          e.target.value === 'Incident' ? undefined : form.serviceCatalogItemId,
                      })}
                  >
                    <option value="Incident">Incidente</option>
                    <option value="Request">Solicitação</option>
                  </select>
                </label>
                {form.ticketType === 'Request' ? (
                  <label className="erp-label lg:col-span-2">
                    Item do catálogo
                    <select
                      className="erp-input"
                      value={form.serviceCatalogItemId ?? ''}
                      onChange={(e) => handleCatalogChange(e.target.value)}
                    >
                      <option value="">Solicitação livre</option>
                      {catalogItems.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <label className="erp-label">
                  Prioridade
                  <select
                    className="erp-input"
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  >
                    <option value="Low">Baixa</option>
                    <option value="Medium">Média</option>
                    <option value="High">Alta</option>
                    <option value="Critical">Crítica</option>
                  </select>
                </label>
                <label className="erp-label">
                  Impacto
                  <select
                    className="erp-input"
                    value={form.impact ?? ''}
                    onChange={(e) => setForm({ ...form, impact: e.target.value || undefined })}
                  >
                    {LEVEL_OPTIONS.map((option) => (
                      <option key={option.value || 'none'} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="erp-label">
                  Urgência
                  <select
                    className="erp-input"
                    value={form.urgency ?? ''}
                    onChange={(e) => setForm({ ...form, urgency: e.target.value || undefined })}
                  >
                    {LEVEL_OPTIONS.map((option) => (
                      <option key={`urgency-${option.value || 'none'}`} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="erp-label">
                  Ticket pai
                  <select
                    className="erp-input"
                    value={form.parentTicketId ?? ''}
                    onChange={(e) => setForm({ ...form, parentTicketId: e.target.value || undefined })}
                  >
                    <option value="">Nenhum</option>
                    {(linkableTickets ?? [])
                      .filter((ticket) => ticket.id !== editing?.id)
                      .map((ticket) => (
                        <option key={ticket.id} value={ticket.id}>
                          {ticket.ticketCode} — {ticket.title}
                        </option>
                      ))}
                  </select>
                </label>
                <label className="erp-label">
                  Produto comercial
                  <select
                    className="erp-input"
                    value={form.salesProductId ?? ''}
                    onChange={(e) => setForm({ ...form, salesProductId: e.target.value || undefined })}
                  >
                    <option value="">Nenhum</option>
                    {salesProducts.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="erp-label lg:col-span-2">
                  Contrato
                  <select
                    className="erp-input"
                    value={form.contractId ?? ''}
                    onChange={(e) => setForm({ ...form, contractId: e.target.value || undefined })}
                  >
                    <option value="">Nenhum</option>
                    {companyContracts.map((contract) => (
                      <option key={contract.id} value={contract.id}>
                        {contract.contractCode} — {contract.title}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="erp-label lg:col-span-2">
                  Problema
                  <select
                    className="erp-input"
                    value={form.problemId ?? ''}
                    onChange={(e) => setForm({ ...form, problemId: e.target.value || undefined })}
                  >
                    <option value="">Nenhum</option>
                    {companyProblems.map((problem) => (
                      <option key={problem.id} value={problem.id}>
                        {problem.problemCode} — {problem.title}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="erp-label">
                  Responsável
                  <select
                    className="erp-input"
                    value={form.assignedToUserId ?? ''}
                    onChange={(e) => setForm({ ...form, assignedToUserId: e.target.value || undefined })}
                  >
                    <option value="">Nenhum</option>
                    {teamMembers.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="erp-label">
                  Equipe
                  <select
                    className="erp-input"
                    value={form.assignedTeamId ?? ''}
                    onChange={(e) => setForm({ ...form, assignedTeamId: e.target.value || undefined })}
                  >
                    <option value="">Nenhuma</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="erp-label">
                  SLA
                  <select
                    className="erp-input"
                    value={form.slaId ?? ''}
                    onChange={(e) => setForm({ ...form, slaId: e.target.value || undefined })}
                  >
                    <option value="">Nenhum</option>
                    {slaOptions.map((sla) => (
                      <option key={sla.id} value={sla.id}>
                        {sla.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="erp-label">
                  Status
                  <select
                    className="erp-input"
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                  >
                    {TICKET_STAGES.map((stage) => (
                      <option key={stage.id} value={stage.id}>
                        {stage.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="erp-label">
                Descrição
                <textarea
                  className="erp-input min-h-[100px]"
                  value={form.description ?? ''}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </label>
              {catalogFormSchema?.fields.length ? (
                <div className="space-y-3 rounded-lg border border-secondary-200 p-3">
                  <p className="text-sm font-medium text-secondary-700">Campos da solicitação</p>
                  {catalogFormSchema.fields.map((field) => (
                    <label key={field.name} className="erp-label">
                      {field.label}
                      {field.required ? (
                        <>
                          {' '}
                          <RequiredMark />
                        </>
                      ) : null}
                      {field.type === 'textarea' ? (
                        <textarea
                          className="erp-input min-h-[80px]"
                          value={formData[field.name] ?? ''}
                          onChange={(e) => updateFormField(field, e.target.value)}
                          required={field.required}
                        />
                      ) : field.type === 'select' ? (
                        <select
                          className="erp-input"
                          value={formData[field.name] ?? ''}
                          onChange={(e) => updateFormField(field, e.target.value)}
                          required={field.required}
                        >
                          <option value="">Selecione...</option>
                          {(field.options ?? []).map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          className="erp-input"
                          type={field.type === 'number' ? 'number' : 'text'}
                          value={formData[field.name] ?? ''}
                          onChange={(e) => updateFormField(field, e.target.value)}
                          required={field.required}
                        />
                      )}
                    </label>
                  ))}
                </div>
              ) : null}
              <div className="erp-btn-actions">
                <button type="button" className="erp-btn-secondary" onClick={() => setFormOpen(false)}>
                  Cancelar
                </button>
                <button type="submit" disabled={saving} className="erp-btn-primary">
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </PageShell>
  );
}
